import * as d3 from 'd3';
import { compact } from 'lodash';

import { GenericObject, boundsExtent, getXValue, getYValue } from '@/charts/helpers';
import { Line, LineChart } from '@/charts/lineChart';
import { isFiniteNumber } from '@/utils';

export type LineArea<D> = Line<D> & {
    yAreaKeyName: keyof D extends string ? keyof D : string;
    yAreaFn?: (datum: D) => number | null;
    yAreaLabel: string;
    color: { line: string; circle: string; area: string };
};

export class LineAreaChart<Datum extends GenericObject, L extends LineArea<Datum> = LineArea<Datum>> extends LineChart<
    Datum,
    LineArea<Datum>
> {
    /**
     * Total height of shaded region which will be centered on the yValue (meaning half above, half below).
     */
    getYArea(datum: Datum, line: LineArea<Datum>) {
        if (!(line.yAreaKeyName in datum) || !line.dataFilter!(datum)) {
            return null;
        }
        if (line.yAreaFn) {
            return line.yAreaFn(datum);
        }
        return getYValue(datum, line.yAreaKeyName);
    }

    getYRange(line: L, data: Datum[]): { min: number; max: number } | null {
        return boundsExtent(
            compact(
                data.map((datum) => {
                    if (!(line.valueKey in datum)) return null;
                    const halfYArea = (this.getYArea(datum, line) || 0) / 2;
                    const yValue = getYValue(datum, line.valueKey) || 0;
                    return {
                        min: yValue - halfYArea,
                        max: yValue + halfYArea,
                    };
                })
            )
        );
    }

    renderLines(dataLines: { line: L; data: Datum[] }[]) {
        dataLines.forEach(({ line, data }) => this.renderArea(line, data));
        dataLines.forEach(({ line, data }) => this.renderLine(line, data));
    }

    renderArea(line: L, data: Datum[]) {
        if (!this.rendered) return;

        const { x, y, svg } = this.rendered;
        const { xAxisKey } = this.options;

        // Create the area generator
        const area = d3
            .area<Datum>()
            .defined((datum) => isFiniteNumber(this.getYArea(datum, line)) && isFiniteNumber(datum[line.valueKey]))
            .x((datum) => x(new Date(getXValue(datum, xAxisKey))))
            .y0((datum) => y(getYValue(datum, line.valueKey) + this.getYArea(datum, line)! / 2))
            .y1((datum) => y(getYValue(datum, line.valueKey) - this.getYArea(datum, line)! / 2));

        svg.append('path').datum(data).attr('fill', line.color.area).attr('d', area);
    }
}

export const drawLineAreaChart = <D extends GenericObject, L extends LineArea<D> = LineArea<D>>({
    containerId,
    dataset,
    lines,
    ...options
}: ConstructorParameters<typeof LineAreaChart<D, L>>[2] & {
    containerId: string;
    dataset: D[];
    lines: L[];
}): LineAreaChart<D, L> => {
    const chart = new LineAreaChart<D, L>(dataset, lines, options);
    chart.render(containerId);
    return chart;
};
